
import React, { useState, useEffect } from "react";
import { useLeads } from "@/context/LeadContext";
import { formatDistanceToNow, format, parseISO, isAfter, subDays } from "date-fns";
import { el } from "date-fns/locale";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Eye, EyeOff, Clock, Calendar, Filter } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { getEmailLogs } from "@/utils/emailUtils";
import { getSMSLogs } from "@/utils/smsUtils";

interface PartnerLeadsListProps {
  partnerId: string;
}

const PartnerLeadsList: React.FC<PartnerLeadsListProps> = ({ partnerId }) => {
  const { leads, refreshLeads } = useLeads();
  const [partnerLeads, setPartnerLeads] = useState<any[]>([]);
  const [filterTimeframe, setFilterTimeframe] = useState<string>("all");
  const [filterService, setFilterService] = useState<string>("all");
  const [filterStage, setFilterStage] = useState<string>("all");
  const [emailStats, setEmailStats] = useState<Record<string, any>>({});
  const [smsStats, setSmsStats] = useState<Record<string, any>>({});
  const [activeTab, setActiveTab] = useState<string>("all");
  
  useEffect(() => {
    refreshLeads();
  }, [refreshLeads]);
  
  // Filter and process leads
  useEffect(() => {
    let filteredLeads = leads.filter(lead => lead.partnerId === partnerId);
    
    // Apply time filter
    if (filterTimeframe !== "all") {
      const now = new Date();
      let cutoffDate;
      
      switch (filterTimeframe) {
        case "week":
          cutoffDate = subDays(now, 7);
          break;
        case "month":
          cutoffDate = subDays(now, 30);
          break;
        case "quarter":
          cutoffDate = subDays(now, 90);
          break;
        default:
          cutoffDate = null;
      }
      
      if (cutoffDate) {
        filteredLeads = filteredLeads.filter(lead => 
          isAfter(parseISO(lead.createdAt), cutoffDate)
        );
      }
    }
    
    // Apply service filter
    if (filterService !== "all") {
      filteredLeads = filteredLeads.filter(lead => lead.service === filterService);
    }
    
    // Apply stage filter
    if (filterStage !== "all") {
      filteredLeads = filteredLeads.filter(lead => lead.stage === filterStage);
    }
    
    // Apply tab filter
    switch (activeTab) {
      case "new":
        filteredLeads = filteredLeads.filter(lead => lead.stage === "Νέο");
        break;
      case "pending":
        filteredLeads = filteredLeads.filter(lead => lead.stage === "σεΕκκρεμότητα");
        break;
      case "closed":
        filteredLeads = filteredLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά");
        break;
      case "info":
        filteredLeads = filteredLeads.filter(lead => lead.stage === "ήθελεΠληροφορίες");
        break;
      default:
        // No additional filtering for "all" tab
        break;
    }
    
    // Sort by most recent first
    filteredLeads.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    
    setPartnerLeads(filteredLeads);
    
    // Fetch email and SMS stats for these leads
    const fetchStats = async () => {
      const emailStatsMap: Record<string, any> = {};
      const smsStatsMap: Record<string, any> = {};
      
      for (const lead of filteredLeads) {
        try {
          // Get email stats
          const emailLogs = await getEmailLogs(partnerId, lead.id);
          const openCount = emailLogs.filter(log => log.event === 'open').length;
          const clickCount = emailLogs.filter(log => log.event.startsWith('click-')).length;
          
          emailStatsMap[lead.id] = { 
            sent: emailLogs.filter(log => log.event === 'sent').length,
            opened: openCount,
            clicked: clickCount
          };
          
          // Get SMS stats
          const smsLogs = await getSMSLogs(partnerId, lead.id);
          const clickedSMS = smsLogs.filter(log => log.event === 'click').length;
          
          smsStatsMap[lead.id] = {
            sent: smsLogs.filter(log => log.status === 'sent').length,
            clicked: clickedSMS
          };
        } catch (error) {
          console.error(`Error fetching stats for lead ${lead.id}:`, error);
        }
      }
      
      setEmailStats(emailStatsMap);
      setSmsStats(smsStatsMap);
    };
    
    fetchStats();
  }, [leads, partnerId, filterTimeframe, filterService, filterStage, activeTab, refreshLeads]);

  // Get unique services for filtering
  const uniqueServices = [...new Set(leads.filter(lead => lead.partnerId === partnerId).map(lead => lead.service))];

  if (partnerLeads.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">Δεν υπάρχουν καταχωρημένες επαφές για αυτόν τον συνεργάτη.</div>
    );
  }

  const getTimeframeLabel = (timeframe: string) => {
    switch (timeframe) {
      case "άμεσα": return "Άμεσα";
      case "εντός3μηνών": return "Εντός 3 μηνών";
      case "κάνωΈρευνα": return "Κάνω έρευνα αγοράς";
      default: return timeframe;
    }
  };
  
  const getServiceLabel = (service: string) => {
    switch (service) {
      case "εξοικονομώ": return "Εξοικονομώ";
      case "αντλίεςΘερμότητας": return "Αντλίες Θερμότητας";
      case "φωτοβολταϊκά": return "Φωτοβολταϊκά";
      case "κλιματιστικά": return "Κλιματιστικά";
      case "άλλο": return "Άλλο";
      default: return service;
    }
  };
  
  const getStageLabel = (stage: string) => {
    switch (stage) {
      case "σεΕκκρεμότητα": return "Σε Εκκρεμότητα";
      case "έκλεισεΗΔουλειά": return "Έκλεισε η Δουλειά";
      case "ήθελεΠληροφορίες": return "Ήθελε Πληροφορίες";
      case "Νέο": return "Νέο";
      default: return stage;
    }
  };

  return (
    <div className="space-y-4">
      {/* Tabs for quick filtering */}
      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full justify-start mb-4">
          <TabsTrigger value="all" className="relative">
            Όλα
            <Badge variant="secondary" className="ml-2 absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center">
              {leads.filter(lead => lead.partnerId === partnerId).length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="new" className="relative">
            Νέα
            <Badge variant="secondary" className="ml-2 absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center">
              {leads.filter(lead => lead.partnerId === partnerId && lead.stage === "Νέο").length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="pending" className="relative">
            Σε Εκκρεμότητα
            <Badge variant="secondary" className="ml-2 absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center">
              {leads.filter(lead => lead.partnerId === partnerId && lead.stage === "σεΕκκρεμότητα").length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="closed" className="relative">
            Έκλεισαν
            <Badge variant="secondary" className="ml-2 absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center">
              {leads.filter(lead => lead.partnerId === partnerId && lead.stage === "έκλεισεΗΔουλειά").length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="info" className="relative">
            Πληροφορίες
            <Badge variant="secondary" className="ml-2 absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center">
              {leads.filter(lead => lead.partnerId === partnerId && lead.stage === "ήθελεΠληροφορίες").length}
            </Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>
      
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <Select value={filterTimeframe} onValueChange={setFilterTimeframe}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Χρονική περίοδος" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Όλες οι περίοδοι</SelectItem>
              <SelectItem value="week">Τελευταία εβδομάδα</SelectItem>
              <SelectItem value="month">Τελευταίος μήνας</SelectItem>
              <SelectItem value="quarter">Τελευταίο τρίμηνο</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center space-x-2">
          <Select value={filterService} onValueChange={setFilterService}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Υπηρεσία" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Όλες οι υπηρεσίες</SelectItem>
              {uniqueServices.map((service) => (
                <SelectItem key={service} value={service}>
                  {getServiceLabel(service)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center space-x-2">
          <Select value={filterStage} onValueChange={setFilterStage}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Κατάσταση" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Όλες οι καταστάσεις</SelectItem>
              <SelectItem value="Νέο">Νέο</SelectItem>
              <SelectItem value="σεΕκκρεμότητα">Σε εκκρεμότητα</SelectItem>
              <SelectItem value="έκλεισεΗΔουλειά">Έκλεισε η δουλειά</SelectItem>
              <SelectItem value="ήθελεΠληροφορίες">Ήθελε πληροφορίες</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Button variant="outline" onClick={() => {
          setFilterTimeframe("all");
          setFilterService("all");
          setFilterStage("all");
          setActiveTab("all");
        }}>
          Καθαρισμός φίλτρων
        </Button>
      </div>

      <div className="overflow-auto max-h-[calc(80vh-240px)]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Όνομα</TableHead>
              <TableHead>Υπηρεσία</TableHead>
              <TableHead>Τηλέφωνο</TableHead>
              <TableHead>Ημερομηνία</TableHead>
              <TableHead>Επείγον</TableHead>
              <TableHead>Κατάσταση</TableHead>
              <TableHead>Επικοινωνία</TableHead>
              <TableHead>Ενέργειες</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {partnerLeads.map((lead) => {
              const timeAgo = formatDistanceToNow(new Date(lead.createdAt), { 
                addSuffix: true,
                locale: el 
              });

              const formattedDate = format(new Date(lead.createdAt), "dd/MM/yyyy HH:mm", { locale: el });
              const emailData = emailStats[lead.id] || { sent: 0, opened: 0, clicked: 0 };
              const smsData = smsStats[lead.id] || { sent: 0, clicked: 0 };

              return (
                <TableRow key={lead.id}>
                  <TableCell className="font-medium">
                    <div className="font-medium">{lead.name}</div>
                    <div className="text-xs text-muted-foreground">{lead.address}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {getServiceLabel(lead.service)}
                    </Badge>
                  </TableCell>
                  <TableCell>{lead.phone}</TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex items-center gap-1 cursor-help">
                            <Clock className="h-3 w-3" />
                            {timeAgo}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            <p>{formattedDate}</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    <Badge variant={lead.timeframe === "άμεσα" ? "destructive" : "outline"}>
                      {getTimeframeLabel(lead.timeframe)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={lead.stage === "έκλεισεΗΔουλειά" ? "default" : 
                           lead.stage === "σεΕκκρεμότητα" ? "warning" : 
                           lead.stage === "ήθελεΠληροφορίες" ? "secondary" : 
                           "outline"}>
                      {getStageLabel(lead.stage)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className={`text-xs flex items-center ${emailData.sent > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              {emailData.sent > 0 ? `${emailData.opened}/${emailData.sent} ανοίχτηκαν` : 'Κανένα email'}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div>
                              <p>Απεστάλησαν: {emailData.sent}</p>
                              <p>Ανοίχτηκαν: {emailData.opened}</p> 
                              <p>Κλικ: {emailData.clicked}</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className={`text-xs flex items-center ${smsData.sent > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                              </svg>
                              {smsData.sent > 0 ? `${smsData.sent} SMS${smsData.clicked > 0 ? ' (κλικ)' : ''}` : 'Κανένα SMS'}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div>
                              <p>Απεστάλησαν: {smsData.sent}</p>
                              <p>Κλικ: {smsData.clicked}</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      <div className="text-xs flex items-center mt-1">
                        {lead.viewed ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex text-green-600">
                                  <Eye className="h-3 w-3 mr-1" />
                                  Προβλήθηκε
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Προβλήθηκε{lead.viewedAt ? ` ${formatDistanceToNow(new Date(lead.viewedAt), { addSuffix: true, locale: el })}` : ""}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex text-gray-400">
                                  <EyeOff className="h-3 w-3 mr-1" />
                                  Δεν προβλήθηκε
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Δεν έχει προβληθεί ακόμα</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Link to={`/lead/${lead.id}`} target="_blank">
                      <Button variant="outline" size="sm">
                        Προβολή
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default PartnerLeadsList;
